/**
 * Los totales de un documento de venta, calculados como los calcula el backend.
 *
 * Existe porque la misma fórmula estaba escrita **ocho veces** —una en el
 * cálculo de totales y otra en el render de cada línea, en los cinco
 * formularios de venta— y las ocho habían divergido entre sí y del backend:
 *
 *   - `DocumentFormPage` redondeaba el subtotal de línea con `Math.round`, que
 *     **trunca a entero cualquier moneda**. En un tenant peruano la pantalla
 *     mostraba un neto sin céntimos mientras el backend guardaba el valor con
 *     ellos, así que `neto + IGV ≠ total` a la vista de quien emite.
 *   - Los otros cuatro **no redondeaban el subtotal por línea**, y eso ya
 *     produce diferencias en Chile: el backend cuantiza línea por línea, así
 *     que un descuento que deja medios pesos (una NC con 25% de descuento) da
 *     un total distinto en pantalla que el que queda guardado. Es el mismo bug
 *     que `DocumentFormPage` había arreglado a mano y nadie replicó.
 *
 * La referencia es `SaleDocumentDetailSerializer.validate()` en
 * `backend/apps/sales/serializers.py`, que es quien decide lo que se guarda.
 * El orden de las operaciones importa y no es intercambiable:
 *
 *     precio_base = redondear(precio_original × tasa)   ← si la línea es extranjera
 *     base        = cantidad × precio_base
 *     subtotal    = redondear(base − base × descuento%)
 *
 * ⚠️ **Para Chile todo esto es un no-op**: `currencyDecimals('CLP')` es 0, así
 * que `roundMoney(x, 'CLP')` y `Math.round(x)` dan exactamente lo mismo. Hay
 * tests que lo fijan en las dos direcciones.
 */
import { roundMoney } from '@/lib/locale'

/** Lo que estos cálculos necesitan de una línea. Cada formulario tiene su propio tipo. */
export interface LineaDeVenta {
  quantity?: string | number | null
  unit_price?: string | number | null
  discount_pct?: string | number | null
  price_currency?: string | null
  is_tax_exempt?: boolean | null
  commission_rate?: string | number | null
}

export interface OpcionesDeTotales {
  /** Moneda en la que el tenant lleva sus montos (`useTenantCurrency()`). */
  baseCurrency: string
  /** Tasa del impuesto del país, en porcentaje (19 en Chile, 18 en Perú). */
  taxRate: number
  /**
   * Tasa de cambio de `currency` a la moneda base, o `null` cuando la línea ya
   * está en la moneda base o no hay tasa cargada.
   *
   * ⚠️ Sin tasa el precio se toma **tal cual**: es lo que hace el backend, y
   * convertir con un número inventado sería peor que no convertir.
   */
  getRate?: (currency: string) => number | null
}

/**
 * Normaliza la línea recibida.
 *
 * 🔴 Las tres funciones por línea aceptan `undefined` **a propósito**, y no es
 * defensivismo gratuito: todos sus llamadores recorren `useFieldArray` y leen
 * los valores de `useWatch`, y esos dos arrays **se desincronizan por un
 * render**. Al agregar una línea, `fields` ya tiene N+1 entradas y el `watch`
 * todavía N, así que `watchedLines[N]` es `undefined` durante ese render.
 *
 * Eso reventó en producción el 2026-09-02 en el formulario de pedidos:
 * «Cannot read properties of undefined (reading 'price_currency')» al agregar
 * la segunda línea de detalle, con la pantalla entera reemplazada por el error
 * boundary y perdiendo lo tipeado. La guarda va acá y no solo en el llamador
 * porque hay cinco formularios llamando a estas funciones y **cuatro tenían
 * `?? {}` y uno no** — con la guarda en el módulo, el descuido no vuelve a ser
 * una caída.
 */
const linea0 = (linea: LineaDeVenta | undefined | null): LineaDeVenta => linea ?? {}

const num = (v: unknown): number => {
  const n = parseFloat(String(v ?? ''))
  return Number.isFinite(n) ? n : 0
}

/**
 * El precio de la línea expresado en la moneda base.
 *
 * ⚠️ Se redondea **antes** de multiplicar por la cantidad, no después: es el
 * orden del backend, y con 10 unidades la diferencia entre las dos formas es
 * visible.
 */
export function precioEnMonedaBase(
  lineaCruda: LineaDeVenta | undefined | null,
  opciones: OpcionesDeTotales,
): number {
  const linea = linea0(lineaCruda)
  const precio = num(linea.unit_price)
  const moneda = linea.price_currency || opciones.baseCurrency
  if (moneda === opciones.baseCurrency) return precio
  const tasa = opciones.getRate?.(moneda) ?? null
  return tasa ? roundMoney(precio * tasa, opciones.baseCurrency) : precio
}

/** El subtotal de una línea, en la moneda base y ya redondeado a sus decimales. */
export function subtotalDeLinea(
  lineaCruda: LineaDeVenta | undefined | null,
  opciones: OpcionesDeTotales,
): number {
  const linea = linea0(lineaCruda)
  const cantidad = num(linea.quantity)
  const precio = precioEnMonedaBase(linea, opciones)
  const descuento = num(linea.discount_pct)
  return roundMoney(cantidad * precio * (1 - descuento / 100), opciones.baseCurrency)
}

/**
 * El subtotal de la línea en su moneda de origen, **sin convertir**.
 *
 * Es lo que se muestra entre paréntesis junto al subtotal convertido, para que
 * quien emite pueda cotejar contra el documento del proveedor. Se redondea con
 * los decimales de ESA moneda, no con los de la base.
 */
export function subtotalDeLineaEnSuMoneda(
  lineaCruda: LineaDeVenta | undefined | null,
  opciones: OpcionesDeTotales,
): number {
  const linea = linea0(lineaCruda)
  const moneda = linea.price_currency || opciones.baseCurrency
  const cantidad = num(linea.quantity)
  const precio = num(linea.unit_price)
  const descuento = num(linea.discount_pct)
  return roundMoney(cantidad * precio * (1 - descuento / 100), moneda)
}

export interface TotalesDelDocumento {
  montoNeto: number
  montoExento: number
  montoIVA: number
  total: number
  lineCount: number
  /** Suma de `subtotal × commission_rate%` de cada línea. Solo la usan los pedidos. */
  comisionTotal: number
}

/**
 * Los totales del documento completo.
 *
 * El impuesto sale del neto ya sumado y no de acumularlo línea por línea: como
 * la tasa es la misma para todas, `redondear(Σsubᵢ × r)` y `Σ(subᵢ × r)`
 * redondeado al final son el mismo número, y es lo que hace el backend.
 */
export function totalesDelDocumento(
  lineas: readonly (LineaDeVenta | undefined | null)[],
  opciones: OpcionesDeTotales,
): TotalesDelDocumento {
  const { baseCurrency, taxRate } = opciones
  let neto = 0
  let exento = 0
  let comision = 0
  let lineCount = 0

  for (const cruda of lineas ?? []) {
    const linea = linea0(cruda)
    const sub = subtotalDeLinea(linea, opciones)
    if (linea.is_tax_exempt) exento += sub
    else neto += sub
    comision += sub * (num(linea.commission_rate) / 100)
    lineCount++
  }

  // Cada subtotal ya está cuantizado, pero sumarlos en punto flotante
  // reintroduce la basura de siempre (0,1 + 0,2 = 0,30000000000000004), que en
  // una moneda con decimales llega hasta la pantalla.
  const montoNeto = roundMoney(neto, baseCurrency)
  const montoExento = roundMoney(exento, baseCurrency)
  const montoIVA = roundMoney((montoNeto * taxRate) / 100, baseCurrency)

  return {
    montoNeto,
    montoExento,
    montoIVA,
    total: roundMoney(montoNeto + montoExento + montoIVA, baseCurrency),
    lineCount,
    comisionTotal: roundMoney(comision, baseCurrency),
  }
}
